import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { clearAllAuthLocal, getStoredDoctorId } from '../utils/auth';
import { DOCTOR_POLL_MS, useIntervalCallback } from '../hooks/useDoctorLiveRefresh';
import './UserNavbar.css';

function DoctorNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [doctor, setDoctor] = useState(null);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('notification/doctor/?limit=5');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const fetchDoctor = async () => {
    try {
      const storedId = getStoredDoctorId();
      let doctorId = storedId;
      let sessionData = null;

      if (!doctorId) {
        const res = await api.get('doctor/session-check/');
        if (!res.data.authenticated) {
          setDoctor(null);
          setDoctorProfile(null);
          return;
        }
        sessionData = res.data;
        doctorId = res.data.doctor_id;
      } else {
        sessionData = { authenticated: true, doctor_id: Number(doctorId) || doctorId };
      }

      setDoctor(sessionData);

      try {
        const profileRes = await api.get(`doctor/profile/${doctorId}/`);
        setDoctorProfile(profileRes.data);
        if (!sessionData.email && profileRes.data?.email) {
          setDoctor((prev) => ({
            ...prev,
            email: profileRes.data.email,
            name: profileRes.data.name,
          }));
        }
      } catch (profileErr) {
        console.error("Failed to fetch doctor profile:", profileErr);
        setDoctorProfile(null);
      }
      fetchNotifications();
    } catch (err) {
      console.error("Doctor Navbar session check failed:", err);
      setDoctor(null);
      setDoctorProfile(null);
    }
  };

  useEffect(() => {
    fetchDoctor();
  }, []); // Once on mount — avoid refetch on every route change

  useIntervalCallback(fetchNotifications, DOCTOR_POLL_MS, !!doctor?.doctor_id);

  useEffect(() => {
    if (!doctor?.doctor_id) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchNotifications();
    };
    window.addEventListener('focus', fetchNotifications);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', fetchNotifications);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [doctor?.doctor_id]);

  // Close dropdown and nav when route changes
  useEffect(() => {
    setDropdownOpen(false);
    setNotificationDropdownOpen(false);
    setIsNavCollapsed(true);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };

    if (dropdownOpen || notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, notificationDropdownOpen]);

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`notification/doctor/mark-read/${notificationId}/`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('notification/doctor/mark-all-read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'APPOINTMENT_BOOKED':
        return 'bi-calendar-plus-fill text-primary';
      case 'APPOINTMENT_CANCELLED':
        return 'bi-calendar-x-fill text-danger';
      case 'APPOINTMENT_RESCHEDULED':
        return 'bi-calendar-event text-warning';
      case 'PAYMENT_SUCCESS':
        return 'bi-credit-card-fill text-success';
      default:
        return 'bi-bell-fill text-info';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getImageUrl = (image) => {
    if (!image) return null;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    return `${apiUrl}/media/${image}`;
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleNavigate = (path) => {
    setDropdownOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      // Call logout API
      await api.post('doctor/logout/');
    } catch (error) {
      // Even if API call fails, proceed with local cleanup
      console.error('Logout API error:', error);
    } finally {
      // Always clear local storage and state
      clearAllAuthLocal();
      setDoctor(null);
      // Navigate to login page
      navigate('/doctor/login', { replace: true });
    }
  };

  const isActive = (path) => location.pathname === path ? 'active fw-bold' : '';

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-primary px-4 shadow-sm sticky-top">
      <div className="container-fluid">
        <span
          className="navbar-brand d-flex align-items-center fw-bold fs-4"
          style={{ cursor: 'pointer', letterSpacing: '1px' }}
          onClick={() => navigate('/doctor/dashboard')}
        >
          <i className="bi bi-heart-pulse-fill me-2 text-white"></i>
          FITNOVA <span className="ms-2 badge bg-light text-primary fs-6">DOCTOR</span>
        </span>

        <button
          className="navbar-toggler"
          type="button"
          onClick={handleNavCollapse}
          aria-controls="doctorNavbar"
          aria-expanded={!isNavCollapsed}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse`} id="doctorNavbar">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0 ms-lg-4">
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/doctor/dashboard')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/doctor/dashboard')}
              >
                Dashboard
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/doctor/create-post')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/doctor/create-post')}
              >
                Create Health Tip
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`nav-link ${isActive('/doctor/posts')}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/doctor/posts')}
              >
                All Posts
              </span>
            </li>
          </ul>

          <div className="d-flex align-items-center gap-3">
            {/* Notification Bell */}
            {doctor && (
              <button
                className="btn p-0 position-relative text-white"
                type="button"
                onClick={() => navigate('/doctor/notifications')}
                style={{ background: 'transparent', border: 'none' }}
                title="View Notifications"
              >
                <i className="bi bi-bell fs-5"></i>
                {unreadCount > 0 && (
                  <span
                    className="position-absolute translate-middle badge rounded-pill bg-danger"
                    style={{ top: '5px', left: '100%', fontSize: '0.65rem' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {doctor ? (
              <div className="dropdown" ref={dropdownRef}>
                <button
                  className="btn p-0 d-flex align-items-center text-white shadow-none"
                  type="button"
                  id="doctorDropdown"
                  onClick={toggleDropdown}
                  aria-expanded={dropdownOpen}
                  style={{ background: 'transparent', border: 'none', gap: '0.5rem' }}
                >
                  {doctorProfile?.image ? (
                    <img
                      src={getImageUrl(doctorProfile.image)}
                      alt={doctor.name || 'Doctor'}
                      className="navbar-user-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) placeholder.style.display = 'inline-block';
                      }}
                    />
                  ) : null}
                  <i
                    className="bi bi-person-badge fs-5 text-white navbar-user-icon"
                    style={{ display: doctorProfile?.image ? 'none' : 'inline-block' }}
                  ></i>
                </button>

                <ul
                  className={`dropdown-menu dropdown-menu-end user-profile-dropdown shadow border-0 mt-2 ${dropdownOpen ? 'show' : ''}`}
                  aria-labelledby="doctorDropdown"
                >
                  <li className="px-3 py-3 border-bottom user-info-header">
                    <div className="d-flex flex-column align-items-center text-center">
                      {doctorProfile?.image ? (
                        <img
                          src={getImageUrl(doctorProfile.image)}
                          alt={doctor.name || 'Doctor'}
                          className="user-profile-image mb-2"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="user-avatar-placeholder mb-2"
                        style={{ display: doctorProfile?.image ? 'none' : 'flex' }}
                      >
                        <i className="bi bi-person-badge"></i>
                      </div>
                      <div className="w-100">
                        <p className="mb-0 fw-bold small text-truncate">{doctor.email}</p>
                      </div>
                    </div>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/doctor/profile')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-person me-2"></i> My Profile
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/doctor/settings')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-gear me-2"></i> Settings
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/doctor/privacy')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-shield-lock me-2"></i> Privacy
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-between"
                      onClick={() => handleNavigate('/doctor/policy')}
                    >
                      <span className="d-flex align-items-center">
                        <i className="bi bi-file-text me-2"></i> Policy
                      </span>
                      <i className="bi bi-chevron-right text-muted"></i>
                    </button>
                  </li>

                  <li><hr className="dropdown-divider" /></li>

                  <li className="px-2 pb-2">
                    <button
                      className="dropdown-item d-flex align-items-center justify-content-center text-white logout-item"
                      onClick={handleLogout}
                      style={{ borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.875rem' }}
                    >
                      <i className="bi bi-box-arrow-right me-1"></i> Logout
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/doctor/login')}>
                Doctor Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default DoctorNavbar;
